import React, {createContext, useContext, useState} from "react";
import {AccountType} from "../models/AccountType";
import {SchoolUser} from "../types";

interface UsersContextType {
    teachers: SchoolUser[];
    students: SchoolUser[];
    guardians: SchoolUser[];
    addNewUser: (newUser: SchoolUser) => void;
    updateUser: (updatedUser: SchoolUser) => void;
    setTeachers: (newTeachers: SchoolUser[]) => void;
    setStudents: (newStudents: SchoolUser[]) => void;
    setGuardians: (newGuardians: SchoolUser[]) => void;
    refreshTrigger: number;
    triggerRefresh: () => void;
}

export const UsersContext = createContext<UsersContextType | null>(null);

export const UsersContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [teachers, setTeachersState] = useState<SchoolUser[]>([]);
    const [students, setStudentsState] = useState<SchoolUser[]>([]);
    const [guardians, setGuardiansState] = useState<SchoolUser[]>([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const triggerRefresh = () => setRefreshTrigger(prev => prev + 1);

    const addNewUser = (newUser: SchoolUser) => {
        switch (newUser.type) {
            case AccountType.TEACHER:
                setTeachersState(prev => [newUser, ...prev]);
                break;
            case AccountType.STUDENT:
                setStudentsState(prev => [newUser, ...prev]);
                break;
            case AccountType.GUARDIAN:
                setGuardiansState(prev => [newUser, ...prev]);
                break;
            default:
                break;
        }
    };

    const updateUser = (updatedUser: SchoolUser) => {
        switch (updatedUser.type) {
            case AccountType.TEACHER:
                setTeachersState(prev => prev.map(user => user.id === updatedUser.id ? updatedUser : user));
                break;
            case AccountType.STUDENT:
                setStudentsState(prev => prev.map(user => user.id === updatedUser.id ? updatedUser : user));
                break;
            case AccountType.GUARDIAN:
                setGuardiansState(prev => prev.map(user => user.id === updatedUser.id ? updatedUser : user));
                break;
            default:
                break;
        }
    };

    const setUsersByType = (newUsers: SchoolUser[], type: string) => {
        switch (type) {
            case AccountType.TEACHER:
                setTeachersState(newUsers);
                break;
            case AccountType.STUDENT:
                setStudentsState(newUsers);
                break;
            case AccountType.GUARDIAN:
                setGuardiansState(newUsers);
                break;
            default:
                break;
        }
    };

    const value: UsersContextType = {
        teachers,
        students,
        guardians,
        addNewUser,
        updateUser,
        setTeachers: (newTeachers) => setUsersByType(newTeachers, AccountType.TEACHER),
        setStudents: (newStudents) => setUsersByType(newStudents, AccountType.STUDENT),
        setGuardians: (newGuardians) => setUsersByType(newGuardians, AccountType.GUARDIAN),
        refreshTrigger,
        triggerRefresh,
    };

    return (
        <UsersContext.Provider value={value}> {children} </UsersContext.Provider>
    );
};

export default UsersContext;

export const useUsers = (): UsersContextType => {
    const ctx = useContext(UsersContext);
    if (!ctx) throw new Error('useUsers must be used within UsersContextProvider');
    return ctx;
};
